import React, { useState, useCallback } from 'react';

/**
 * Equabot Auth & Token Management component (Spec 088).
 * Handles Gateway token generation, display, and WhatsApp QR placeholder.
 */

interface EquabotAuthProps {
    token?: string;
    tokenExpiry?: string;
    onSaveToken?: (token: string) => Promise<boolean> | boolean;
}

const EquabotAuth: React.FC<EquabotAuthProps> = ({ token, tokenExpiry, onSaveToken }) => {
    const [newToken, setNewToken] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleSave = useCallback(async () => {
        if (!newToken.trim() || !onSaveToken) return;
        setSaving(true);
        const ok = await onSaveToken(newToken.trim());
        if (ok) setNewToken('');
        setSaving(false);
    }, [newToken, onSaveToken]);

    const handleCopy = useCallback(() => {
        if (token) {
            navigator.clipboard.writeText(token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }, [token]);

    const fieldGroupStyle: React.CSSProperties = {
        marginBottom: '20px',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block',
        fontSize: '11px',
        fontWeight: 600,
        color: '#8899A6',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '6px',
    };

    const tokenDisplayStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: '#2B3B44',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#C8D6DF',
        wordBreak: 'break-all',
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '8px 12px',
        fontSize: '13px',
        backgroundColor: '#2B3B44',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '6px',
        color: '#E8F0FE',
        outline: 'none',
        boxSizing: 'border-box' as const,
    };

    const btnStyleBase: React.CSSProperties = {
        padding: '6px 14px',
        fontSize: '12px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'opacity 0.15s ease',
    };

    const primaryBtnStyle: React.CSSProperties = {
        ...btnStyleBase,
        backgroundColor: '#33AA40',
        color: '#fff',
    };

    const secondaryBtnStyle: React.CSSProperties = {
        ...btnStyleBase,
        backgroundColor: 'rgba(255,255,255,0.1)',
        color: '#C8D6DF',
    };

    const infoStyle: React.CSSProperties = {
        fontSize: '11px',
        color: '#6B7F8E',
        marginTop: '4px',
    };

    const sectionDividerStyle: React.CSSProperties = {
        borderTop: '1px solid rgba(255,255,255,0.08)',
        margin: '24px 0',
    };

    const qrPlaceholderStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderRadius: '8px',
        border: '1px dashed rgba(255,255,255,0.15)',
        color: '#6B7F8E',
        fontSize: '13px',
        textAlign: 'center',
        gap: '12px',
    };

    return (
        <>
            {/* Current Token */}
            <div style={fieldGroupStyle}>
                <label style={labelStyle}>Current Gateway Token</label>
                {token ? (
                    <>
                        <div style={tokenDisplayStyle}>
                            <span style={{ flex: 1 }}>
                                {showToken ? token : `${token.slice(0, 8)}${'•'.repeat(16)}${token.slice(-4)}`}
                            </span>
                            <button
                                style={secondaryBtnStyle}
                                onClick={() => setShowToken(!showToken)}
                            >
                                {showToken ? 'Hide' : 'Show'}
                            </button>
                            <button
                                style={secondaryBtnStyle}
                                onClick={handleCopy}
                            >
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                        {tokenExpiry && (
                            <p style={infoStyle}>
                                Expires: {new Date(tokenExpiry).toLocaleDateString()}
                            </p>
                        )}
                    </>
                ) : (
                    <p style={{ ...infoStyle, color: '#FF8A8A' }}>
                        No token configured. Enter a token below to connect to the Gateway.
                    </p>
                )}
            </div>

            {/* Update Token */}
            <div style={fieldGroupStyle}>
                <label style={labelStyle}>Update Token</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                        style={{ ...inputStyle, flex: 1 }}
                        type="password"
                        placeholder="Paste new gateway token…"
                        value={newToken}
                        onChange={(e) => setNewToken(e.target.value)}
                    />
                    <button
                        style={{ ...primaryBtnStyle, opacity: !newToken.trim() || saving ? 0.5 : 1 }}
                        onClick={handleSave}
                        disabled={!newToken.trim() || saving}
                    >
                        {saving ? '…' : 'Save'}
                    </button>
                </div>
                <p style={infoStyle}>
                    Token is stored in the Gateway configuration and used for all API authentication.
                </p>
            </div>

            <div style={sectionDividerStyle} />

            {/* WhatsApp QR Placeholder */}
            <div style={fieldGroupStyle}>
                <label style={labelStyle}>WhatsApp Pairing</label>
                <div style={qrPlaceholderStyle}>
                    <span style={{ fontSize: '32px' }}>📱</span>
                    <span>WhatsApp QR pairing will be available<br />when the Gateway supports it.</span>
                    <button style={secondaryBtnStyle} disabled>
                        Generate QR Code
                    </button>
                </div>
                <p style={infoStyle}>
                    Scan the QR code with WhatsApp to link this Equabot instance as a messaging endpoint.
                </p>
            </div>
        </>
    );
};

export default EquabotAuth;
